import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { Contatti } from "./Contatti";

export const ServicePage = ({ testid, kicker, title, intro, heroImg, services, gallery, closingLine }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  return (
    <div data-testid={testid} className="relative">
      <section ref={ref} className="relative h-[85vh] overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={heroImg} alt={title} className="h-full w-full object-cover img-cinematic" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        </motion.div>
        <div className="relative z-10 flex h-full items-end pb-20">
          <div className="cx-container">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }} className="overline">
              {kicker}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4 font-display text-6xl font-light leading-none text-[var(--cx-white)] sm:text-7xl lg:text-8xl"
            >
              {title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.6 }}
              className="mt-6 max-w-xl font-body text-lg leading-relaxed text-[var(--cx-white)]/75"
            >
              {intro}
            </motion.p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="cx-container">
          <Reveal><p className="overline">I nostri servizi</p></Reveal>
          <div className="mt-12 border-t border-[var(--cx-border)]">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.04}>
                <div className="group grid grid-cols-1 items-baseline gap-3 border-b border-[var(--cx-border)] py-7 transition-colors duration-500 hover:bg-[var(--cx-surface)] md:grid-cols-12 md:gap-8 md:px-4">
                  <span className="font-body text-sm text-[var(--cx-gold)]/50 md:col-span-1">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-3xl font-light text-[var(--cx-white)] md:col-span-5 md:text-4xl">{s.title}</h3>
                  <p className="font-body text-base text-[var(--cx-muted)] md:col-span-6">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {gallery?.length > 0 && (
        <section className="pb-24 md:pb-32">
          <div className="cx-container grid gap-6 md:grid-cols-2">
            {gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.06} className={i === 0 ? "md:row-span-2" : ""}>
                <div className="h-full overflow-hidden">
                  <img src={g} alt="" className="h-full w-full object-cover img-cinematic transition-transform duration-[1200ms] hover:scale-105" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="py-20">
        <div className="cx-container text-center">
          <Reveal>
            <p className="mx-auto max-w-3xl font-display text-3xl font-light leading-tight text-[var(--cx-white)] md:text-5xl">
              {closingLine}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/#contatti" data-testid={`${testid}-cta`}
              className="mt-10 inline-flex items-center gap-4 bg-[var(--cx-gold)] px-9 py-4 font-body text-sm tracking-wide text-black transition-all duration-300 hover:bg-[var(--cx-gold-bright)]"
            >
              Parlaci del tuo progetto <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      <Contatti />
    </div>
  );
};
